/*
  Copyright (c) 2025 Fluxonix Inc. All rights reserved.
*/

// platform_console_admin_ui/src/api/incidents.js

import { jwtDecode } from "jwt-decode";
import { api } from "../api";

function isTokenValid(token) {
  try {
    const { exp } = jwtDecode(token);
    return exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

export function getValidIncidentToken() {
  const token = localStorage.getItem("fluxonix_token");

  if (token && isTokenValid(token)) {
    return token;
  }

  return null;
}

export function getAuthHeaders() {
  const token = getValidIncidentToken();

  if (!token) {
    throw new Error("Session expired. Please log in again.");
  }

  return {
    Authorization: `Bearer ${token}`,
  };
}

export async function createIncident(payload = {}) {
  const res = await api.post("/api/secure/incident/create", payload, {
    headers: getAuthHeaders(),
  });

  return res.data;
}

export async function getMyIncidents() {
  const res = await api.get("/api/secure/incident/my", {
    headers: getAuthHeaders(),
  });

  return res.data;
}

export async function getIncident(incidentId) {
  const res = await api.get(`/api/secure/incident/${incidentId}`, {
    headers: getAuthHeaders(),
  });

  return res.data;
}

export async function getRelatedOperationalMemory(incidentId) {
  const res = await api.get(`/api/secure/incident/${incidentId}/related-memory`, {
    headers: getAuthHeaders(),
  });
  return res.data;
}

export async function updateIncident(incidentId, payload) {
  const res = await api.patch(`/api/secure/incident/${incidentId}`, payload, {
    headers: getAuthHeaders(),
  });

  return res.data;
}

export async function uploadIncidentAudio(incidentId, file, durationSeconds = null) {
  const formData = new FormData();
  formData.append("file", file);

  const config = {
    headers: {
      ...getAuthHeaders(),
      "Content-Type": "multipart/form-data",
    },
    params: {},
  };

  if (durationSeconds !== null && durationSeconds !== undefined) {
    config.params.duration_seconds = durationSeconds;
  }

  const res = await api.post(`/api/secure/incident/${incidentId}/upload-audio`, formData, config);

  return res.data;
}

export async function uploadIncidentEvidence(incidentId, file) {
  const formData = new FormData();
  formData.append("file", file);

  const res = await api.post(`/api/secure/incident/${incidentId}/upload-evidence`, formData, {
    headers: {
      ...getAuthHeaders(),
      "Content-Type": "multipart/form-data",
    },
  });

  return res.data;
}

export async function submitIncidentForProcessing(incidentId, payload) {
  const res = await api.post(
    `/api/secure/incident/${incidentId}/submit-for-processing`,
    payload,
    {
      headers: getAuthHeaders(),
    }
  );

  return res.data;
}

export async function getIncidentNcrDraft(incidentId) {
  const res = await api.get(`/api/secure/incident/${incidentId}/ncr-draft`, {
    headers: getAuthHeaders(),
  });

  return res.data;
}

export async function getReviewQueue() {
  const res = await api.get("/api/secure/incident/reviews", {
    headers: getAuthHeaders(),
  });

  return res.data;
}

export async function approveIncident(incidentId, payload = {}) {
  const res = await api.post(
    `/api/secure/incident/${incidentId}/approve`,
    payload,
    {
      headers: getAuthHeaders(),
    }
  );

  return res.data;
}

export async function rejectIncident(incidentId, payload = {}) {
  const res = await api.post(
    `/api/secure/incident/${incidentId}/reject`,
    payload,
    {
      headers: getAuthHeaders(),
    }
  );

  return res.data;
}

export async function deleteIncidentVoiceCapture(incidentId, voiceId) {
  const res = await api.delete(
    `/api/secure/incident/${incidentId}/voice/${voiceId}`,
    {
      headers: getAuthHeaders(),
    }
  );

  return res.data;
}

export async function deleteIncidentEvidence(incidentId, evidenceId) {
  const res = await api.delete(
    `/api/secure/incident/${incidentId}/evidence/${evidenceId}`,
    {
      headers: getAuthHeaders(),
    }
  );

  return res.data;
}

export async function getCorrectiveActionOversightQueue() {
  const response = await api.get(
    "/api/secure/incident/corrective-action-oversight",
    { headers: getAuthHeaders(), timeout: 15000 }
  );
  return response.data;
}

export async function verifyIncidentWorkItem(
  incidentId,
  workItemId,
  verificationNotes = ""
) {
  const response = await api.post(
    `/api/secure/incident-workspaces/${incidentId}/work-items/${workItemId}/verify`,
    {
      verification_notes: verificationNotes.trim() || null,
    }
  );

  return response.data;
}
